// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../styles/CardDetail.css";
import { getCardMarket, getCardSoldStats } from "../api/cards";
import {
  buildCardDetailUrl,
  formatRelativeTime,
  getTradeActivityForCard,
} from "../utils/marketAnalytics";

const buildSparklinePath = (series, width, height, padding) => {
  if (series.length < 2) return "";
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;

  return series
    .map((value, index) => {
      const x = padding + (index / (series.length - 1)) * (width - padding * 2);
      const y = padding + (1 - (value - min) / range) * (height - padding * 2);
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
};

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "—";
};

const titleCase = (value) => {
  if (!value) return "";
  return value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default function CardDetail() {
  const navigate = useNavigate();
  const { cardId } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const cardNameFromRoute = location.state?.cardName || params.get("name") || titleCase(cardId);
  const gameFromRoute = location.state?.game || params.get("game") || "";
  const photoUrlFromState = location.state?.photoUrl || null;
  const condition = location.state?.condition || null;
  const gradingCompany = location.state?.gradingCompany || null;
  const gradeNumber = location.state?.gradeNumber || null;

  const resolvedCardId = useMemo(() => location.state?.cardId || cardId, [location.state, cardId]);
  const [marketData, setMarketData] = useState<any>(null);
  const [soldStats, setSoldStats] = useState<any>(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const cardFromApi = marketData?.card || null;
  const cardName = cardNameFromRoute || cardFromApi?.cardName || titleCase(cardId);
  const game = gameFromRoute || cardFromApi?.game || "";
  const photoUrl = photoUrlFromState || cardFromApi?.imageUrl || params.get("photoUrl") || null;
  const market = useMemo(() => marketData?.market || {}, [marketData]);
  const series = useMemo(() => market?.series || [], [market]);
  const trend = useMemo(() => {
    if (series.length < 2) return "stable";
    const delta = Number(series[series.length - 1] || 0) - Number(series[0] || 0);
    if (delta > 0) return "up";
    if (delta < 0) return "down";
    return "stable";
  }, [series]);
  const trendSymbol = trend === "up" ? "↑" : trend === "down" ? "↓" : "→";
  const tradeActivity = useMemo(
    () => getTradeActivityForCard(resolvedCardId).slice(0, 5),
    [resolvedCardId]
  );
  const sparklinePath = useMemo(
    () => buildSparklinePath(series, 260, 90, 8),
    [series]
  );
  const graphStats = useMemo(() => {
    if (!series.length) {
      return {
        minValue: null,
        maxValue: null,
        startValue: null,
        endValue: null,
      };
    }
    return {
      minValue: Math.min(...series),
      maxValue: Math.max(...series),
      startValue: series[0],
      endValue: series[series.length - 1],
    };
  }, [series]);
  const graphPoints = useMemo(() => {
    if (series.length < 2) return [];
    const width = 260;
    const height = 90;
    const padding = 8;
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = max - min || 1;
    return series.map((value, index) => {
      const x = padding + (index / (series.length - 1)) * (width - padding * 2);
      const y = padding + (1 - (value - min) / range) * (height - padding * 2);
      return {
        x: Number(x.toFixed(2)),
        y: Number(y.toFixed(2)),
        value,
      };
    });
  }, [series]);
  const showAllGraphPoints = graphPoints.length > 0 && graphPoints.length <= 12;
  const firstPoint = graphPoints[0] || null;
  const lastPoint = graphPoints.length ? graphPoints[graphPoints.length - 1] : null;

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!resolvedCardId || !game) return;
      setMarketLoading(true);
      try {
        const [marketRes, soldRes] = await Promise.all([
          getCardMarket({ game, cardId: resolvedCardId }),
          getCardSoldStats({ game, cardId: resolvedCardId }),
        ]);
        if (!active) return;
        setMarketData(marketRes);
        setSoldStats(soldRes);
      } catch {
        if (!active) return;
        setMarketData(null);
        setSoldStats(null);
      } finally {
        if (active) setMarketLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [resolvedCardId, game]);

  return (
    <div className="card-detail-page">
      <div className="card-detail-header">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>
          ←
        </button>
        <div>
          <h1>{cardName}</h1>
          <p>{game || "Card details"}</p>
          {(cardFromApi?.setName || cardFromApi?.cardNumber || cardFromApi?.rarity) && (
            <div className="card-submeta">
              {cardFromApi?.setName && <span>{cardFromApi.setName}</span>}
              {cardFromApi?.cardNumber && <span>{cardFromApi.cardNumber}</span>}
              {cardFromApi?.rarity && <span>{cardFromApi.rarity}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="card-detail-grid">
        <div className="card-visual">
          <div className="card-image-frame">
            {photoUrl ? (
              <img src={photoUrl} alt={cardName} />
            ) : (
              <div className="card-placeholder">No card image available for this card yet.</div>
            )}
          </div>
          <div className="card-meta">
            {condition && <span className="meta-pill">{condition}</span>}
            {gradingCompany && (
              <span className="meta-pill">{gradingCompany.toUpperCase()}</span>
            )}
            {gradeNumber && <span className="meta-pill">Grade {gradeNumber}</span>}
          </div>
        </div>

        <div className="card-analytics">
          <section className="analytics-section">
            <div className="section-header">
              <div className="section-title">
                <h2>Market Analytics</h2>
                <span className="beta-badge" aria-label="beta">BETA</span>
              </div>
              <span className={`trend-pill ${trend}`}>
                {trendSymbol} {trend}
              </span>
            </div>
            <div className="market-value">
              {marketLoading
                ? "Loading market..."
                : market?.low != null && market?.high != null
                  ? `Estimated value: ${formatMoney(market.low)}-${formatMoney(market.high)}`
                  : "Estimated value: Unavailable"}
            </div>
            <div className="market-metrics">
              <div className="metric-chip">
                <span>Low</span>
                <strong>{formatMoney(market?.low)}</strong>
              </div>
              <div className="metric-chip">
                <span>Mid</span>
                <strong>{formatMoney(market?.mid)}</strong>
              </div>
              <div className="metric-chip">
                <span>High</span>
                <strong>{formatMoney(market?.high)}</strong>
              </div>
              <div className="metric-chip">
                <span>Market</span>
                <strong>{formatMoney(market?.market)}</strong>
              </div>
            </div>
            <div className="sparkline">
              <svg width="260" height="90" viewBox="0 0 260 90" aria-hidden="true">
                <line className="sparkline-grid" x1="8" y1="8" x2="252" y2="8" />
                <line className="sparkline-grid" x1="8" y1="45" x2="252" y2="45" />
                <line className="sparkline-grid" x1="8" y1="82" x2="252" y2="82" />
                {series.length >= 2 && (
                  <path d={sparklinePath} fill="none" stroke="#7cf3ff" strokeWidth="2" />
                )}
                {series.length === 1 && (
                  <>
                    <circle cx="130" cy="45" r="4" fill="#7cf3ff" />
                    <text className="sparkline-value-label" x="138" y="39">
                      {formatMoney(series[0])}
                    </text>
                  </>
                )}
                {showAllGraphPoints &&
                  graphPoints.map((point, index) => (
                    <circle
                      key={`point-${index}`}
                      cx={point.x}
                      cy={point.y}
                      r="2.2"
                      fill="#7cf3ff"
                    />
                  ))}
                {firstPoint && (
                  <circle cx={firstPoint.x} cy={firstPoint.y} r="3" fill="#a5b4fc" />
                )}
                {lastPoint && (
                  <circle cx={lastPoint.x} cy={lastPoint.y} r="3.2" fill="#22d3ee" />
                )}
              </svg>
              {!marketLoading && series.length >= 2 && (
                <div className="sparkline-legends">
                  <span>Start: {formatMoney(graphStats.startValue)}</span>
                  <span>Latest: {formatMoney(graphStats.endValue)}</span>
                </div>
              )}
              {!marketLoading && series.length >= 2 && (
                <div className="sparkline-legends">
                  <span>Min: {formatMoney(graphStats.minValue)}</span>
                  <span>Max: {formatMoney(graphStats.maxValue)}</span>
                </div>
              )}
              <div className="sparkline-label">
                {marketLoading
                  ? "Loading market history..."
                  : series.length === 0
                    ? "No market history yet."
                    : series.length === 1
                      ? "Only one snapshot available."
                      : "Last 60 days (approx.)"}
              </div>
            </div>
            <div className="analytics-note">
              {market?.fetchedAt ? `Last updated: ${formatRelativeTime(market.fetchedAt)}` : "No live market snapshot yet."}
            </div>
          </section>

          <section className="analytics-section">
            <div className="section-header">
              <div className="section-title">
                <h2>Cardex Trade Activity</h2>
                <span className="beta-badge" aria-label="beta">BETA</span>
              </div>
              <span className="section-pill">Global</span>
            </div>
            {tradeActivity.length === 0 ? (
              <div className="empty-state">No recent trades logged for this card yet.</div>
            ) : (
              <div className="activity-feed">
                {tradeActivity.map((entry) => (
                  <div key={entry.id} className="activity-row">
                    <div className="activity-text">
                      {entry.relatedCard
                        ? `Traded for ${entry.relatedCard}`
                        : `Traded as part of a ${entry.dealSize}-card deal`}
                    </div>
                    <div className="activity-time">
                      {formatRelativeTime(entry.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {soldStats && soldStats.totalSales > 0 && (
              <div className="analytics-note">
                Sold comps: {soldStats.totalSales || 0} sales · avg ${soldStats.avgPrice || 0}
              </div>
            )}
            {soldStats && soldStats.totalSales === 0 && (
              <div className="analytics-note">
                No sold comps available yet for this card.
              </div>
            )}
          </section>

          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(buildCardDetailUrl(resolvedCardId, cardName, game))}
          >
            Share card insights
          </button>
        </div>
      </div>
    </div>
  );
}
